import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';

const ResetPassword = () => {
  const navigate = useNavigate();

  // 'loading' | 'form' | 'success' | 'invalid'
  const [pageState, setPageState] = useState('loading');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // True when the user signed up via a social provider only (no email/password identity)
  const [isSocialOnlyAccount, setIsSocialOnlyAccount] = useState(false);

  useEffect(() => {
    // Supabase detects the recovery token from the URL hash automatically
    // (detectSessionInUrl: true). We listen for the PASSWORD_RECOVERY event,
    // which fires once Supabase has exchanged the token for a valid session.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'PASSWORD_RECOVERY') {
          // Check whether this account was social-only (no email/password identity).
          // session.user.identities is an array of linked providers.
          const identities = session?.user?.identities ?? [];
          const hasEmailIdentity = identities.some((id) => id.provider === 'email');
          setIsSocialOnlyAccount(!hasEmailIdentity);
          // Token is valid — show the new-password form
          setPageState('form');
        } else if (event === 'SIGNED_IN' && session) {
          // Already signed in without recovery — redirect away
          navigate('/dashboard', { replace: true });
        }
      }
    );

    // If Supabase already processed the hash before our listener registered,
    // check whether we already have a recovery session via getSession.
    let fallbackTimer = null;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        // A session was created from the recovery link — show the form.
        // (If it wasn't a recovery link the onAuthStateChange handler above
        // will have already redirected to /dashboard.)
        const identities = session?.user?.identities ?? [];
        const hasEmailIdentity = identities.some((id) => id.provider === 'email');
        setIsSocialOnlyAccount(!hasEmailIdentity);
        setPageState('form');
      } else {
        // Give the onAuthStateChange a moment to fire from the URL hash.
        // If nothing fires within 3 s, the link is invalid/expired.
        fallbackTimer = setTimeout(() => {
          setPageState((prev) => (prev === 'loading' ? 'invalid' : prev));
        }, 3000);
      }
    });

    return () => {
      subscription.unsubscribe();
      if (fallbackTimer) clearTimeout(fallbackTimer);
    };
  }, [navigate]);

  const validate = () => {
    const newErrors = {};
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setPageState('success');
    } catch (err) {
      console.error('Password update error:', err);
      setSubmitError(err?.message || 'Failed to update password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Reset Password - WebForms</title>
        <meta name="description" content="Set a new password for your WebForms account." />
      </Helmet>

      <div className="min-h-screen bg-background flex">
        {/* Left panel — same branding as /authentication */}
        <div className="hidden lg:flex lg:w-1/2 bg-primary/5 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-accent/10" />
          <div className="relative z-10 flex flex-col justify-between p-8 lg:p-12">
            <div className="flex items-center gap-3">
              <div className="w-60 h-auto rounded-md flex items-center justify-center overflow-hidden">
                <Image
                  src="/assets/images/web-forms-logo-1769801123836.png"
                  alt="WebForms.biz logo"
                  className="w-60 h-auto object-contain"
                />
              </div>
            </div>

            <div className="space-y-6 max-w-lg">
              <h1 className="text-4xl lg:text-5xl font-heading font-bold text-foreground leading-tight">
                Secure Your Account
              </h1>
              <p className="text-lg text-muted-foreground">
                Choose a strong, unique password to keep your forms and data safe.
              </p>

              <div className="grid grid-cols-2 gap-4 pt-4">
                {[
                  { icon: 'Shield', label: 'Encrypted', description: 'Bank-level security' },
                  { icon: 'Lock', label: 'Private', description: 'Your data stays yours' },
                  { icon: 'RefreshCw', label: 'Always Accessible', description: 'Reset anytime' },
                  { icon: 'CheckCircle2', label: 'Verified', description: 'Email-verified flow' },
                ].map((feature, index) => (
                  <div key={index} className="flex gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-md flex items-center justify-center flex-shrink-0">
                      <Icon name={feature.icon} size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground">{feature.label}</div>
                      <div className="text-xs text-muted-foreground">{feature.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div />
          </div>
        </div>

        {/* Right panel */}
        <div className="flex-1 flex items-center justify-center p-4 md:p-8">
          <div className="w-full max-w-md">
            {/* Mobile logo */}
            <div className="lg:hidden mb-8 text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-60 h-auto rounded-md flex items-center justify-center overflow-hidden">
                  <Image
                    src="/assets/images/web-forms-logo-1769800970615.png"
                    alt="WebForms.biz logo"
                    className="w-60 h-auto object-contain"
                  />
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg shadow-elevation-md p-6 md:p-8">
              {/* Loading */}
              {pageState === 'loading' && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                    <Icon name="Loader2" size={32} color="var(--color-primary)" />
                  </div>
                  <h2 className="text-xl font-heading font-semibold text-foreground mb-2">
                    Verifying your link…
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Please wait while we validate your password reset link.
                  </p>
                </div>
              )}

              {/* Invalid / expired */}
              {pageState === 'invalid' && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="AlertCircle" size={32} color="var(--color-destructive)" />
                  </div>
                  <h2 className="text-xl font-heading font-semibold text-foreground mb-2">
                    Link Expired or Invalid
                  </h2>
                  <p className="text-sm text-muted-foreground mb-6">
                    This password reset link has expired or has already been used.
                    Request a new one from the sign-in page.
                  </p>
                  <Button
                    variant="default"
                    fullWidth
                    onClick={() => navigate('/authentication')}
                  >
                    Back to Sign In
                  </Button>
                </div>
              )}

              {/* New password form */}
              {pageState === 'form' && (
                <>
                  <div className="mb-6">
                    <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
                      Set New Password
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Choose a strong password for your account.
                    </p>
                  </div>

                  {isSocialOnlyAccount && (
                    <div className="mb-4 p-4 bg-primary/10 border border-primary/20 rounded-md flex gap-3">
                      <Icon name="Info" size={18} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-foreground">
                        Your account uses social sign-in (e.g. Google). Setting a password here will
                        <strong> add</strong> email/password login to your account — your social login
                        will continue to work.
                      </p>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4">
                    {submitError && (
                      <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                        <p className="text-sm text-destructive">{submitError}</p>
                      </div>
                    )}

                    <Input
                      label="New Password"
                      type="password"
                      placeholder="At least 8 characters"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (errors.password) setErrors((prev) => ({ ...prev, password: '' }));
                      }}
                      error={errors.password}
                      required
                    />

                    <Input
                      label="Confirm New Password"
                      type="password"
                      placeholder="Repeat your password"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (errors.confirmPassword) setErrors((prev) => ({ ...prev, confirmPassword: '' }));
                      }}
                      error={errors.confirmPassword}
                      required
                    />

                    <Button
                      type="submit"
                      variant="default"
                      size="lg"
                      loading={isSubmitting}
                      fullWidth
                    >
                      Update Password
                    </Button>
                  </form>
                </>
              )}

              {/* Success */}
              {pageState === 'success' && (
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="CheckCircle2" size={32} color="var(--color-success)" />
                  </div>
                  <h2 className="text-xl font-heading font-semibold text-foreground mb-2">
                    {isSocialOnlyAccount ? 'Password Added' : 'Password Updated'}
                  </h2>
                  <p className="text-sm text-muted-foreground mb-6">
                    {isSocialOnlyAccount
                      ? 'A password has been added to your account. You can now sign in with either your social login or your email and new password.'
                      : 'Your password has been changed successfully. You can now sign in with your new password.'}
                  </p>
                  <Button
                    variant="default"
                    fullWidth
                    onClick={() => navigate('/authentication')}
                  >
                    Go to Sign In
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
